/**
 * utils/sorFileManager.js
 * Utilitaire pour parser les noms de fichiers .sor et extraire les informations
 */

export class SorFileManager {
  /**
   * Parser un nom de fichier .sor et extraire les informations
   * @param {string} fileName - Nom du fichier .sor
   * @returns {Object|null} Informations extraites ou null si invalid
   */
  static parseFileName(fileName) {
    try {
      // Suppression de l'extension .sor
      const nameWithoutExt = fileName.replace(/\.sor$/i, "");

      // Pattern pour capturer : (pbo)_(B2-T?-M?-???)_OE_(longueurOnde)
      const pattern = /^(.+)_(B2-T(\w)-M(\w)-(\d{3}))_OE_(\d+)$/;
      const match = nameWithoutExt.match(pattern);

      if (!match) {
        return null;
      }

      const [, pbo, , tiroir, moduleChar, port, longueurOnde] = match;

      return {
        fileName: fileName,
        tiroir: `T${tiroir}`,
        module: `MODULE_${moduleChar}`,
        port: parseInt(port, 10),
        longueurOnde: longueurOnde,
        pbo: pbo,
      };
    } catch (error) {
      return null;
    }
  }

  static extractPBOFromPath(filePath) {
    if (!filePath || !filePath.includes("/")) {
      return null;
    }

    // Extraire le dossier parent (première partie du path)
    const folderName = filePath.split("/")[0];

    if (!folderName) {
      return null;
    }

    // Si c'est un nombre (ex: "001", "025"), le convertir en int
    if (/^\d+$/.test(folderName)) {
      return parseInt(folderName, 10);
    }

    // Sinon, retourner la string complète
    return folderName;
  }

  /**
   * Grouper les fichiers par Tiroirs/Modules/Ports
   */

  static groupByLocation(files) {
    const grouped = {};

    files.forEach((file) => {
      const parsed = this.parseFileName(file.name);
      if (!parsed) return;

      const { tiroir, module, port } = parsed;

      if (!grouped[tiroir]) grouped[tiroir] = {};
      if (!grouped[tiroir][module]) grouped[tiroir][module] = {};
      if (!grouped[tiroir][module][`Port_${port}`]) {
        grouped[tiroir][module][`Port_${port}`] = {
          port,
          files: [],
          mesures: {},
          pboValues: new Set(),
          folderPBOs: new Set(), // Ajouter les PBO des dossiers
        };
      }

      const fiber = grouped[tiroir][module][`Port_${port}`];

      // Extraire le PBO du dossier
      const folderPBO = this.extractPBOFromPath(file.path);

      fiber.files.push({ ...file, parsed, folderPBO });
      fiber.mesures[parsed.longueurOnde] = file;
      fiber.pboValues.add(parsed.pbo);

      if (folderPBO !== null) {
        fiber.folderPBOs.add(folderPBO);
      }
    });

    return grouped;
  }

  /**
   * Valider PBO pour une fibre (cohérence fichier + dossier vs fichier)
   */

  static validatePBO(pboValues, folderPBOs) {
    const uniqueFilePBOs = Array.from(pboValues);
    const uniqueFolderPBOs = Array.from(folderPBOs);

    // Cas 1: Aucun PBO trouvé
    if (uniqueFilePBOs.length === 0) {
      return {
        isValid: false,
        type: "no_pbo",
        message: "Aucun PBO trouvé",
      };
    }

    // Cas 2: Plusieurs PBO différents dans les fichiers
    if (uniqueFilePBOs.length > 1) {
      return {
        isValid: false,
        type: "multiple_file_pbo",
        message: `Plusieurs PBO fichiers: ${uniqueFilePBOs.join(", ")}`,
        filePBOs: uniqueFilePBOs,
      };
    }

    // Cas 3: Plusieurs dossiers différents
    if (uniqueFolderPBOs.length > 1) {
      return {
        isValid: false,
        type: "multiple_folder_pbo",
        message: `Plusieurs dossiers: ${uniqueFolderPBOs.join(", ")}`,
        folderPBOs: uniqueFolderPBOs,
      };
    }

    const filePBO = uniqueFilePBOs[0];
    const folderPBO = uniqueFolderPBOs[0];

    // Cas 4: Pas de dossier PBO détectable
    if (folderPBO === null || folderPBO === undefined) {
      return {
        isValid: true, // On considère OK si on ne peut pas vérifier
        type: "no_folder_check",
        message: `${filePBO}`,
        pbo: filePBO,
      };
    }

    // Cas 5: PBO fichier ≠ PBO dossier
    if (filePBO !== folderPBO) {
      return {
        isValid: false,
        type: "folder_file_mismatch",
        message: `Folder: ${folderPBO}, Fibre: ${filePBO}`,
        folderPBO: folderPBO,
        filePBO: filePBO,
      };
    }

    // Cas 6: Tout OK
    return {
      isValid: true,
      type: "ok",
      message: `${filePBO}`,
      pbo: filePBO,
    };
  }
}
